import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import { LeadFormActions } from '../../Services/_actions/index.js';
import { dateFormate, indianTimeZone } from '../../utils/helper.jsx';
import CommonTable from '../Shared/Tables/TableV1.tsx';

interface AllUpdateStatusModalProps {
  open: boolean;
  onClose: () => void;
  selectedRow?: any;
  leadFormId: string;
}

interface statusObj {
  _id?: string;
  created_by?: string;
  role?: string;
  status_id?: string;
  status_name?: string;
  template_name?: string;
  updated_at?: string;
  comment?: string;
  payload?: Record<string, any>;
  type_of_status?: string;
}

const AllUpdateStatusModal: React.FC<AllUpdateStatusModalProps> = ({
  open,
  onClose,
  selectedRow,
  leadFormId,
}) => {
  const actions = LeadFormActions();
  const { showSnackBar } = useSnackBar();

  const [statusList, setStatusList] = useState<statusObj[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPayloadFields, setSelectedPayloadFields] = useState<string[]>([]);
  const [selectedTab, setSelectedTab] = useState('all');

  // Row states
  const [rowNewComments, setRowNewComments] = useState<Record<string, string>>({});
  const [rowLoading, setRowLoading] = useState<Record<string, boolean>>({});
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const handlePopoverOpen = (e: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget);
  const handlePopoverClose = () => setAnchorEl(null);
  const openPopover = Boolean(anchorEl);

  const fetchStatusList = async () => {
    if (!selectedRow?.id) return;
    try {
      const res: any = await actions.fetchLeadStatus1({ id: selectedRow?.id || '' });
      if (res?.status === 200) {
        setStatusList(res?.data || []);
      } else {
        showSnackBar('Failed to fetch status list', 'error');
      }
    } catch (error) {
      console.error(error);
      showSnackBar(error?.toString() || 'Error fetching status list', 'error');
    }
  };

  useEffect(() => {
    if (open && selectedRow?.id) fetchStatusList();
  }, [open, selectedRow]);

  const payloadKeys = useMemo(() => {
    const allPayloads = statusList.map((s) => s.payload || {});
    const keysSet = new Set<string>();
    allPayloads.forEach((p) => Object.keys(p).forEach((k) => keysSet.add(k)));
    return Array.from(keysSet);
  }, [statusList]);

  const dynamicPayloadColumns = useMemo(() => {
    return selectedPayloadFields.map((key) => ({
      accessorKey: `payload.${key}`,
      header: `Payload: ${key}`,
      size: 160,
      Cell: ({ row }: any) => {
        const value = row?.original?.payload?.[key];
        return <>{value !== undefined && value !== null ? String(value) : '-'}</>;
      },
    }));
  }, [selectedPayloadFields]);

  const handleFieldToggle = (field: string) => {
    setSelectedPayloadFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  };

  // ====================== Columns =====================
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: 'index',
        header: 'S.No.',
        size: 50,
        enableSorting: false,
        Cell: ({ row }: any) => row.index + 1,
      },
      { accessorKey: 'status_name', header: 'Status', size: 140 },
      { accessorKey: 'template_name', header: 'Template', size: 140 },
      { accessorKey: 'created_by', header: 'Created By', size: 140 },
      {
        accessorKey: 'updated_at',
        header: 'Date Time',
        size: 160,
        Cell: ({ row }: any) => {
          const updatedAt = row?.original?.updated_at;
          return updatedAt
            ? dateFormate(indianTimeZone(updatedAt), 'DD-MM-YYYY hh:mm A')
            : '-';
        },
      },
      {
        accessorKey: 'comment',
        header: 'Comment',
        size: 320,
        Cell: ({ row }: any) => {
          const rowKey = row.original._id || row.original.status_id;
          const comment = row.original.comment ?? '';
          const newComment = rowNewComments[rowKey] ?? '';
          const loading = rowLoading[rowKey] ?? false;
          const expanded = expandedRows[rowKey] ?? false;

          const words = comment?.split(' ') || [];
          const shortComment = words.slice(0, 5).join(' ');

          const toggleExpanded = () =>
            setExpandedRows((prev) => ({ ...prev, [rowKey]: !expanded }));

          const handleInputChange = (value: string) => {
            setRowNewComments((prev) => ({ ...prev, [rowKey]: value }));
          };

          const handleCommentSave = async () => {
            if (!newComment.trim()) {
              showSnackBar('Comment cannot be empty', 'warning');
              return;
            }
            setRowLoading((prev) => ({ ...prev, [rowKey]: true }));

            try {
              const res: any = await actions.updateLeadStatusComment({
                id: selectedRow?.id,
                comment: newComment,
                status_id: row.original.status_id,
              });

              if (res?.status === 200) {
                await fetchStatusList(); // refresh full data
                setRowNewComments((prev) => ({ ...prev, [rowKey]: '' }));
                showSnackBar('Comment added successfully.', 'success');
              } else {
                showSnackBar('Failed to add comment.', 'error');
              }
            } catch (err) {
              showSnackBar('Error adding comment.', 'error');
            } finally {
              setRowLoading((prev) => ({ ...prev, [rowKey]: false }));
            }
          };

          return (
            <Box>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {expanded ? comment : shortComment}
                {words.length > 5 && (
                  <Box
                    component="span"
                    sx={{ color: 'blue', ml: 1, cursor: 'pointer' }}
                    onClick={toggleExpanded}
                  >
                    {expanded ? 'Show Less' : 'View More'}
                  </Box>
                )}
              </Typography>

              <Box mt={1} display="flex" gap={1}>
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => handleInputChange(e.target.value)}
                  placeholder="Add comment"
                  style={{
                    flexGrow: 1,
                    padding: '4px 8px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleCommentSave}
                  disabled={loading || !newComment.trim()}
                >
                  Save
                </Button>
              </Box>
            </Box>
          );
        },
      },
    ];
    return [...baseColumns, ...dynamicPayloadColumns];
  }, [
    dynamicPayloadColumns,
    selectedRow?.id,
    rowNewComments,
    rowLoading,
    expandedRows,
  ]);

  // ================= Tabs ==================
  const typeTabs = useMemo(() => {
    const uniqueTypes = Array.from(
      new Set(statusList.map((s) => s.type_of_status).filter(Boolean))
    );
    return ['all', ...uniqueTypes];
  }, [statusList]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const filteredStatusList = useMemo(() => {
    if (selectedTab === 'all') return statusList;
    return statusList.filter((s) => s.type_of_status === selectedTab);
  }, [statusList, selectedTab]);

  useEffect(() => {
    if (typeTabs.length > 1 && selectedTab === 'all') {
      setSelectedTab(typeTabs[1]);
    }
  }, [typeTabs]);

  // ================= Render ==================
  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          p: 4,
          width: { xs: '90%', sm: '80%', md: '70%', lg: '1000px' },
          maxHeight: '95vh',
          overflowY: 'auto',
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          flexWrap="wrap"
        >
          <Typography variant="h6" fontWeight="bold" whiteSpace="nowrap">
            Status Logs
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Button variant="outlined" onClick={handlePopoverOpen} size="small">
              Select Payload Columns
            </Button>
            <Popover
              open={openPopover}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Box sx={{ p: 2, maxHeight: 300, overflowY: 'auto' }}>
                {payloadKeys.length === 0 ? (
                  <Typography>No payload keys found</Typography>
                ) : (
                  <FormGroup>
                    {payloadKeys.map((field) => (
                      <FormControlLabel
                        key={field}
                        control={
                          <Checkbox
                            checked={selectedPayloadFields.includes(field)}
                            onChange={() => handleFieldToggle(field)}
                          />
                        }
                        label={field}
                      />
                    ))}
                  </FormGroup>
                )}
              </Box>
            </Popover>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs value={selectedTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          {typeTabs
            .filter((type) => type !== 'all')
            .map((type) => (
              <Tab key={type} label={type} value={type} />
            ))}
        </Tabs>

        <CommonTable
          columns={columns}
          data={filteredStatusList}
          muiTableContainerProps={{
            sx: {
              height: 'calc(100vh - 300px)',
              width: '100%',
              overflowY: 'auto',
              '&::-webkit-scrollbar': { width: '6px' },
            },
          }}
          enableStickyHeader
          enableBottomToolbar={false}
        />
      </Box>
    </Modal>
  );
};

export default AllUpdateStatusModal;
